<template>
  <section
    class="categorise-content h-full"
    :class="{
      'flex-col': isShowCorrectAnswer,
      'presenting': isPresentingOrBackstage
    }"
  >
    <section v-if="!isShowCorrectAnswer" class="options-section">
      <div>
        <span>
          {{ $i18nPresenter.t('Options') }}
        </span>
        <span class="total-options">{{ options.length }}</span>
      </div>
      <section class="options">
        <div
          v-for="(option, index) in options"
          :key="index"
          class="option"
        >
          {{ option.value }}
        </div>
      </section>
    </section>
    <section class="category-section" :class="layoutClass">
      <div
        v-for="(category, index) in categories"
        :key="category.id"
        class="category"
        :style="{
          '--hover-color': categoryHoverColorMap[index],
          '--active-color': categoryActiveColorMap[index],
          '--background-color': categoryColorMap[index],
          borderColor: categoryColorMap[index],
          backgroundColor: categoryBackgroundColorMap[index],
        }"
        @click="selectedCategory = category"
      >
        <div
          class="title"
        >
          <span class="left-block">
            <span
              class="category-annotation"
              :style="{
                color: categoryAnnotationColorMap[index],
              }"
            >{{ categoryAnnotationMap[index] }}</span>
            <!-- Todo: Break into component -->
            <span class="category-name" v-if="category.value">{{ category.value }}</span>
            <span class="opacity-50" v-else>
              {{ `${$i18nPresenter.t('Category')} ${categoryAnnotationMap[index]}` }}
            </span>
          </span>
          <span v-if="isShowCorrectAnswer && category.options && category.options.length">
            ({{ category.options.length }})
          </span>
        </div>
        <div
          class="options"
        >
          <div
            v-for="(option, id) in sortedOptions(category)"
            :key="id"
            class="option"
          >
            <div class="mb-2">
              {{ option.value }}
            </div>
            <div v-if="isShowCorrectAnswer">
              <Answer
                :numberOfCorrectAnswers="correctAnswerCountPerOption(category, option)"
                :numberOfIncorrectAnswers="incorrectAnswerCountPerOption(category, option)"
                :isShowUserIcon="false"
                :size="'sm'"
                class="w-fit"
              ></Answer>
            </div>
          </div>
        </div>
      </div>
    </section>
    <section class="answer-result" v-if="isShowCorrectAnswer">
      <Answer
        :numberOfCorrectAnswers="correctedAnswerPerSlide"
        :numberOfIncorrectAnswers="incorrectedAnswerPerSlide"
      ></Answer>
    </section>
    <CategoriseModal
      v-if="selectedCategory && selectedCategory.options.length > 0"
      @hide="selectedCategory = null"
      :category="selectedCategory"
      :options="sortedOptions"
      :isShowCorrectAnswer="isShowCorrectAnswer"
      :correctAnswerCountPerOption="correctAnswerCountPerOption"
      :incorrectAnswerCountPerOption="incorrectAnswerCountPerOption"
    />
  </section>
</template>

<script>
import CategoriseModal from './CategoriesModal.vue'
import Answer from '@/components-v2/presentation-editor/middle/common/Answer.vue'
import {
  CATEGORY_COLOR_MAP,
  CATEGORY_BACKGROUND_COLOR_MAP,
  CATEGORY_ANNOTATION_MAP,
  CATEGORY_ANNOTATION_COLOR_MAP,
  CATEGORY_HOVER_COLOR_MAP,
  CATEGORY_ACTIVE_COLOR_MAP,
} from '@/constant/Categorise.constant'

const twoRowLayoutApplyRules = [5, 6, 7, 8]
const twoColumnLayoutApplyRules = [2]
const threeColumnLayoutApplyRules = [3, 5, 6]
const fourColumnLayoutApplyRules = [4, 7, 8]

export default {
  name: 'CategoriseContentView',
  components: {
    CategoriseModal,
    Answer,
  },
  props: {
    categories: {
      type: Array,
      required: true,
      default: () => [],
    },
    options: {
      type: Array,
      required: true,
      default: () => [],
    },
    isPresentingOrBackstage: {
      type: Boolean,
      default: false,
    },
    correctedAnswerPerSlide: {
      type: Number,
      default: 0,
    },
    incorrectedAnswerPerSlide: {
      type: Number,
      default: 0,
    },
    correctAnswerCountPerOption: {
      type: Function,
      default: () => {},
    },
    incorrectAnswerCountPerOption: {
      type: Function,
      default: () => {},
    },
    isShowCorrectAnswer: {
      type: Boolean,
      default: false,
    },

  },
  data() {
    return {
      selectedCategory: null,
      categoryColorMap: CATEGORY_COLOR_MAP,
      categoryBackgroundColorMap: CATEGORY_BACKGROUND_COLOR_MAP,
      categoryAnnotationMap: CATEGORY_ANNOTATION_MAP,
      categoryAnnotationColorMap: CATEGORY_ANNOTATION_COLOR_MAP,
      categoryHoverColorMap: CATEGORY_HOVER_COLOR_MAP,
      categoryActiveColorMap: CATEGORY_ACTIVE_COLOR_MAP,
    }
  },
  computed: {
    layoutClass() {
      let rowClass = 'one-row'
      let columnClass = ''
      const categoryCount = this.categories.length
      if (twoRowLayoutApplyRules.includes(categoryCount)) {
        rowClass = 'two-row'
      }

      if (twoColumnLayoutApplyRules.includes(categoryCount)) {
        columnClass = 'two-column'
      }

      if (threeColumnLayoutApplyRules.includes(categoryCount)) {
        columnClass = 'three-column'
      }

      if (fourColumnLayoutApplyRules.includes(categoryCount)) {
        columnClass = 'four-column'
      }

      if (this.isShowCorrectAnswer) return `${rowClass} show-answer-category-layout ${columnClass}`
      return `${rowClass} ${columnClass}`
    },
    sortedOptions() {
      return (category) => {
        if (!this.isShowCorrectAnswer) return category.options
        const optionsSortedByCorrectness = [...category.options]
        optionsSortedByCorrectness.sort((opt1, opt2) => {
          const opt1Correctness = this.correctAnswerCountPerOption(category, opt1) -
            this.incorrectAnswerCountPerOption(category, opt1)
          const opt2Correctness = this.correctAnswerCountPerOption(category, opt2) -
            this.incorrectAnswerCountPerOption(category, opt2)
          return opt2Correctness - opt1Correctness
        })
        return optionsSortedByCorrectness
      }
    },
  },

}

</script>

<style lang="scss" scoped>
  .categorise-content {
    display: flex;
    gap: 0.75rem;
    padding: 18px;
    max-height: 100%;

    & > .options-section {
      width: 270px;
      min-width: 270px;
      background-color: #F0F4FA;
      border: 1px solid #D3D7E1;
      border-radius: 8px;

      & > div {
        font-weight: 700;
        font-size: 20px;
        color: #525F7F;
        display: flex;
        align-items: center;
        justify-content: center;
        padding-block: 20px;
        gap: 0.75rem;

        & > .total-options {
            color: #282E3E;
            font-size: 1rem;
            background-color: white;
            padding: 0.5rem;
            line-height: 1;
            display: inline-flex;
            align-items: center;
            justify-content: center;
        }
      }

      & > .options {
        max-height: calc(100% - 84px);
      }
    }

    & > .category-section {
        flex: 1;
        display: grid;
        gap: 0.75rem;
        grid-template-columns: repeat(2, 1fr);

        &.show-answer-category-layout {
          max-height: calc(100% - 52px);
        }

        & > .category {

            border: 2px solid;
            border-radius: 8px;
            & > .title {
              transition: background-color ease 0.3s;
              background-color: var(--background-color);

                cursor: pointer;
                font-size: 20px;
                color: white;
                font-weight: 700;
                display: flex;
                justify-content: space-between;
                align-items: center;
                gap: 0.75rem;
                padding: 0.75rem;
                height: 72px;

                & .left-block {
                  display: flex;
                  gap: 0.75rem;
                }

                & .category-annotation {
                    font-size: 20px;
                    width: 28px;
                    height: 28px;
                    background-color: white;
                    padding: 4px;
                    border-radius: 4px;
                    display: inline-flex;
                    align-items: center;
                    align-self: center;
                    justify-content: center;
                }

                & .category-name {
                  line-height: 24px;
                }
            }

            & > .options {
                max-height: calc(100% - 72px);
                padding: 0.75rem;
            }
        }
    }

    & > .answer-result {
      align-self: center;
    }

    .options {
        padding: 0 0.75rem 0.75rem 0.75rem;
        overflow-y: auto;
        display: flex;
        flex-direction: column;
        gap: 0.5rem;

        & > .option {
            font-size: 18px;
            color: #282E3E;
            padding: 0.75rem;
            background-color: white;
            border: 1px solid #D3D7E1;
            border-radius: 8px;
        }
      }
    .one-row {
        grid-template-rows: 100%;
    }

    .two-row {
        grid-template-rows: repeat(2, calc(50% - 0.75rem/2));
    }

    .two-column {
        grid-template-columns: repeat(2, 1fr);
    }

    .three-column {
        grid-template-columns: repeat(3, 1fr);
    }

    .four-column {
        grid-template-columns: repeat(4, 1fr);
    }
  }
  .categorise-content.presenting {
    .category {
      &:hover {
        border-color: var(--hover-color);
      }
      &:active {
        border-color: var(--active-color);
      }
    }
    .category .title {
      &:hover {
        background-color: var(--hover-color);
      }
      &:active {
        background-color: var(--active-color);
      }
    }
  }
  </style>
